import { Controller } from "@hotwired/stimulus"

// Enhanced hover card controller for beautiful client cards
export default class extends Controller {
  static targets = ["progressBar", "settingsButton", "statusIndicator"]
  static values = { 
    keywords: Number, 
    categories: Number,
    maxKeywords: { type: Number, default: 100 },
    maxCategories: { type: Number, default: 20 }
  }

  connect() {
    this.setupEventListeners()
    this.animateProgressBars()
  }

  disconnect() {
    this.teardownEventListeners()
  }

  setupEventListeners() {
    this.boundOnHover = this.onHover.bind(this)
    this.boundOnLeave = this.onLeave.bind(this)
    this.boundOnFocus = this.onFocus.bind(this)
    this.boundOnBlur = this.onBlur.bind(this)

    this.element.addEventListener('mouseenter', this.boundOnHover)
    this.element.addEventListener('mouseleave', this.boundOnLeave)
    this.element.addEventListener('focusin', this.boundOnFocus)
    this.element.addEventListener('focusout', this.boundOnBlur)
  }

  teardownEventListeners() {
    this.element.removeEventListener('mouseenter', this.boundOnHover)
    this.element.removeEventListener('mouseleave', this.boundOnLeave)
    this.element.removeEventListener('focusin', this.boundOnFocus)
    this.element.removeEventListener('focusout', this.boundOnBlur)
  }

  onHover() {
    // Add subtle scale and glow effect
    this.element.classList.add('transform', 'scale-[1.02]')
    
    // Enhance shadow
    this.element.classList.remove('shadow-lg')
    this.element.classList.add('shadow-2xl')
    
    // Animate progress bars on hover
    this.animateProgressBars(true)
    
    // Add subtle rotation to settings cog
    const settingsButton = this.element.querySelector('[data-controller*="hover-card"] svg')
    if (settingsButton) {
      settingsButton.classList.add('rotate-12')
    }
  }

  onLeave() {
    // Remove hover effects
    this.element.classList.remove('transform', 'scale-[1.02]')
    this.element.classList.remove('shadow-2xl')
    this.element.classList.add('shadow-lg')
    
    // Reset progress bars
    this.animateProgressBars(false)
    
    // Reset settings cog rotation
    const settingsButton = this.element.querySelector('[data-controller*="hover-card"] svg')
    if (settingsButton) {
      settingsButton.classList.remove('rotate-12')
    }
  }

  onFocus() {
    // Similar to hover but more pronounced for accessibility
    this.element.classList.add('transform', 'scale-[1.03]')
    this.element.classList.add('ring-2', 'ring-theme-accent', 'ring-opacity-50')
  }

  onBlur() {
    this.element.classList.remove('transform', 'scale-[1.03]')
    this.element.classList.remove('ring-2', 'ring-theme-accent', 'ring-opacity-50')
  }

  animateProgressBars(enhanced = false) {
    const progressBars = this.element.querySelectorAll('[style*="width:"]')
    
    progressBars.forEach((bar, index) => {
      if (enhanced) {
        // Add a subtle pulse effect on hover
        bar.classList.add('animate-pulse')
        
        // Slightly increase the width for visual feedback
        const currentWidth = parseFloat(bar.style.width)
        if (currentWidth > 0) {
          bar.style.width = `${Math.min(currentWidth * 1.1, 100)}%`
        }
      } else {
        // Reset to original state
        bar.classList.remove('animate-pulse')
        
        // Reset width based on actual data
        if (index === 0) {
          // Keywords progress bar
          const keywordWidth = this.calculateKeywordProgress()
          bar.style.width = `${keywordWidth}%`
        } else if (index === 1) {
          // Categories progress bar
          const categoryWidth = this.calculateCategoryProgress()
          bar.style.width = `${categoryWidth}%`
        }
      }
    })
  }

  calculateKeywordProgress() {
    if (!this.hasKeywordsValue || this.keywordsValue === 0) return 0
    return Math.min((this.keywordsValue / this.maxKeywordsValue) * 100, 100)
  }

  calculateCategoryProgress() {
    if (!this.hasCategoriesValue || this.categoriesValue === 0) return 0
    return Math.min((this.categoriesValue / this.maxCategoriesValue) * 100, 100)
  }

  // Handle keyboard navigation
  keydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      const link = this.element.querySelector('a[aria-label*="Open configuration"]')
      if (link) {
        link.click()
      }
    }
  }
} 